import { RESTAPIError } from './RESTAPIError'
import { URLConstructor } from '../../../utils/URLBuilder'
import CLanguageHandler from '../../../Languages/LanguageHandler'
import { LanguageErrors } from '../../../../../i18n'
import { isStringNotEmpty, isVarNotEmpty } from '../../../utils/GlobalFunctions'
import { JSONResponse, RESTOptions } from '../../Types/Types'
import { RequestResults, RequestResultWrapper } from '../../Types/MainMenu/ReportListTypes'
import { SUCCESS } from '../../../constants/ResponseResultTypes'
import { RequestCacheHandler } from './RequestCacheHandler'
import { RequestCache } from './RequestCache'

const requestCacheHandler: RequestCacheHandler = new RequestCacheHandler()

/**
 * Get JSON response from REST endpoint with caching mechanism.
 * <T> - The response type you are expecting from the server.
 *
 * @param requestPath - The path to request data from.
 * @param options - The REST options, this includes headers and cache ttl.
 *
 * @returns - Promise<JSONResponse<T>> - Promise of the data from the server in the generic type specified.
 */
export const requestJSON = <T extends unknown, U extends Nullable<object> = object>(
    requestPath: URLConstructor,
    options: RESTOptions<U>
): Promise<JSONResponse<T>> => {
    // Constructed URL from request path.
    const constructedURL: string = requestPath.constructURL()

    // Get the request cache by URL.
    const requestCache: RequestCache = isVarNotEmpty(options?.cacheTime)
        ? requestCacheHandler.cacheByURL(constructedURL, options?.body, { ttl: options.cacheTime })
        : RequestCache.newInstance()

    // Fetch the data from the server using the RequestCache fetch function.
    return requestCache.fetch<T, U>(constructedURL, options)
}

/**
 * Purges a cache entry for the given URL.
 *
 * @param url - The URL you wish to purge the cache for.
 */
export const purgeCacheForURL = (url: string): void => requestCacheHandler.purgeCacheForURL(url)

/**
 * Maps standard reporting request result object to its result data.
 */
export const mapRequestResults = <T extends unknown>(
    response: JSONResponse<RequestResults<T>>,
    isErrorIgnored?: boolean
): Definable<T> => {
    const language: CLanguageHandler = CLanguageHandler.getInstance()
    language.loadBundle(LanguageErrors.NAME)

    const results: Definable<RequestResultWrapper<T>[]> = response?.RequestResults

    let result: RequestResultWrapper<T>['RequestResult']

    if (Array.isArray(results)) {
        result = results[0].RequestResult
    } else {
        // In case of bug in the backend the response will not contain the result.
        throw new RESTAPIError(0, null, language.get(LanguageErrors.RestJsonProcessing), null)
    }

    const resultType: Definable<number> = result?.Type

    switch (resultType) {
        case SUCCESS:
            // Successful response.
            return result?.ResultData
        default:
            // Error cases that have to be handled by calling logic.
            if (isErrorIgnored) {
                return result?.ResultData
            }

            throw new RESTAPIError(
                0,
                null,
                result && isStringNotEmpty(result.Error) ? result.Error : language.get(LanguageErrors.UnhandlableError),
                null
            )
    }
}
